import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CubeIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores/uiStore';
import { useProducts, useProductCategories, useUpdateProduct } from '@/hooks/useProducts';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { SearchInput } from '@/components/ui/SearchInput';
import { Select } from '@/components/ui/Select';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { SkeletonTable } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { ProductImage } from '@/components/ui/ProductImage';
import { formatCurrency } from '@/utils/format';
import type { NormalizedProductListItem } from '@wooconnect/shared';

export function ProductsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeSiteId = useUIStore((s) => s.activeSiteId);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(0);
  const [editProduct, setEditProduct] = useState<NormalizedProductListItem | null>(null);
  const [editForm, setEditForm] = useState({ regularPrice: '', salePrice: '', stockQuantity: '', stockStatus: '', status: '' });
  const [editError, setEditError] = useState('');

  const filters = { page, perPage, search: search || undefined, status: statusFilter || undefined, stockStatus: stockFilter || undefined, category: categoryFilter || undefined };
  const { data, isLoading, isError, refetch } = useProducts(activeSiteId, filters);
  const { data: categories } = useProductCategories(activeSiteId);
  const updateMutation = useUpdateProduct(activeSiteId ?? '');

  const handleSearch = useCallback((val: string) => { setSearch(val); setPage(1); }, []);

  const statusOptions = [
    { value: '', label: t('common.all') },
    { value: 'publish', label: t('products.published') },
    { value: 'draft', label: t('products.draft') },
  ];

  const stockOptions = [
    { value: '', label: t('common.all') },
    { value: 'instock', label: t('products.inStock') },
    { value: 'outofstock', label: t('products.outOfStock') },
    { value: 'onbackorder', label: t('products.lowStock') },
  ];

  const categoryOptions = [
    { value: '0', label: t('common.all') },
    ...(categories ?? []).map((c) => ({ value: String(c.id), label: c.name })),
  ];

  const openQuickEdit = (product: NormalizedProductListItem) => {
    setEditProduct(product);
    setEditError('');
    setEditForm({
      regularPrice: product.regularPrice || product.price || '',
      salePrice: product.salePrice || '',
      stockQuantity: product.stockQuantity != null ? String(product.stockQuantity) : '',
      stockStatus: product.stockStatus || 'instock',
      status: product.status || 'publish',
    });
  };

  const handleQuickSave = async () => {
    if (!editProduct) return;
    setEditError('');

    // Validation
    if (editForm.regularPrice && editForm.salePrice && Number(editForm.salePrice) >= Number(editForm.regularPrice)) {
      setEditError(t('products.salePriceMustBeLower'));
      return;
    }
    if (editForm.stockQuantity !== '' && Number(editForm.stockQuantity) < 0) {
      setEditError(t('products.stockCannotBeNegative'));
      return;
    }

    try {
      await updateMutation.mutateAsync({
        productId: editProduct.id,
        data: {
          regular_price: editForm.regularPrice || undefined,
          sale_price: editForm.salePrice,
          stock_quantity: editForm.stockQuantity !== '' ? Number(editForm.stockQuantity) : undefined,
          stock_status: editForm.stockStatus as 'instock' | 'outofstock' | 'onbackorder' || undefined,
          status: editForm.status as 'publish' | 'draft',
        },
      });
      setEditProduct(null);
    } catch {
      // Error toast is shown by the mutation hook
    }
  };

  if (!activeSiteId) return <EmptyState icon={CubeIcon} title={t('dashboard.selectStore')} />;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('products.title')}</h1>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput value={search} onChange={handleSearch} placeholder={`${t('common.search')}...`} className="w-full sm:w-64" />
        <Select options={statusOptions} value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} placeholder={t('products.status')} className="w-full sm:w-40" />
        <Select options={stockOptions} value={stockFilter} onChange={(v) => { setStockFilter(v); setPage(1); }} placeholder={t('products.stock')} className="w-full sm:w-40" />
        <Select options={categoryOptions} value={String(categoryFilter)} onChange={(v) => { setCategoryFilter(Number(v)); setPage(1); }} placeholder={t('products.category')} className="w-full sm:w-44" />
      </div>

      <Card padding="sm">
        {isLoading ? (
          <SkeletonTable rows={10} cols={6} />
        ) : isError ? (
          <ErrorState onRetry={() => refetch()} />
        ) : data && data.items.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-3"></th>
                    <th className="px-4 py-3">{t('products.name')}</th>
                    <th className="px-4 py-3">{t('products.sku')}</th>
                    <th className="px-4 py-3">{t('products.price')}</th>
                    <th className="px-4 py-3">{t('products.stock')}</th>
                    <th className="px-4 py-3">{t('products.status')}</th>
                    <th className="px-4 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((product) => (
                    <tr key={product.id} className="border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3">
                        <ProductImage src={product.image} alt={product.name} />
                      </td>
                      <td className="cursor-pointer px-4 py-3 font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400" onClick={() => navigate(`/products/${product.id}`)}>
                        {product.name}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{product.sku || '-'}</td>
                      <td className="px-4 py-3">
                        {product.onSale && product.salePrice ? (
                          <div>
                            <span className="text-gray-400 line-through">{formatCurrency(product.regularPrice)}</span>
                            <span className="ml-1 font-medium text-green-600 dark:text-green-400">{formatCurrency(product.salePrice)}</span>
                          </div>
                        ) : (
                          <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(product.price)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={product.stockStatus === 'outofstock' ? 'font-medium text-red-600' : product.stockQuantity != null && product.stockQuantity < 5 ? 'font-medium text-yellow-600' : 'text-green-600'}>
                          {product.stockQuantity ?? (product.stockStatus === 'instock' ? t('products.inStock') : product.stockStatus === 'outofstock' ? t('products.outOfStock') : product.stockStatus === 'onbackorder' ? t('products.lowStock') : product.stockStatus)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge label={product.status === 'publish' ? t('products.published') : t('products.draft')} variant={product.status === 'publish' ? 'success' : 'default'} />
                      </td>
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="sm" onClick={() => openQuickEdit(product)}>{t('common.edit')}</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 px-4">
              <Pagination page={data.page} perPage={data.perPage} total={data.total} totalPages={data.totalPages} onPageChange={setPage} onPerPageChange={(pp) => { setPerPage(pp); setPage(1); }} />
            </div>
          </>
        ) : (
          <EmptyState icon={CubeIcon} title={search || statusFilter ? t('common.noResults') : t('products.noProducts')} actionLabel={search || statusFilter ? t('common.retry') : undefined} onAction={search || statusFilter ? () => { setSearch(''); setStatusFilter(''); } : undefined} />
        )}
      </Card>

      {/* Quick Edit Modal */}
      <Modal isOpen={!!editProduct} onClose={() => setEditProduct(null)} title={t('products.quickEdit')} size="sm">
        {editProduct && (
          <div className="space-y-4">
            <p className="font-medium text-gray-900 dark:text-gray-100">{editProduct.name}</p>
            {editError && <p className="text-sm text-red-500">{editError}</p>}
            <Input label={t('products.regularPrice')} type="number" step="0.01" min="0" value={editForm.regularPrice} onChange={(e) => setEditForm((f) => ({ ...f, regularPrice: e.target.value }))} />
            <Input label={t('products.salePrice')} type="number" step="0.01" min="0" value={editForm.salePrice} onChange={(e) => setEditForm((f) => ({ ...f, salePrice: e.target.value }))} placeholder={t('products.leaveEmptyToRemoveSale')} />
            <Input label={t('products.stockQuantity')} type="number" min="0" value={editForm.stockQuantity} onChange={(e) => setEditForm((f) => ({ ...f, stockQuantity: e.target.value }))} />
            <Select label={t('products.stockStatus')} options={[{ value: 'instock', label: t('products.inStock') }, { value: 'outofstock', label: t('products.outOfStock') }, { value: 'onbackorder', label: t('products.onBackorder') }]} value={editForm.stockStatus} onChange={(v) => setEditForm((f) => ({ ...f, stockStatus: v }))} />
            <Select label={t('products.status')} options={[{ value: 'publish', label: t('products.published') }, { value: 'draft', label: t('products.draft') }]} value={editForm.status} onChange={(v) => setEditForm((f) => ({ ...f, status: v }))} />
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setEditProduct(null)}>{t('common.cancel')}</Button>
              <Button onClick={handleQuickSave} loading={updateMutation.isPending}>{t('common.save')}</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
